import cn from 'classnames';
import type { ComponentProps } from 'react';
import {
  type BorderRadiusKey,
  type PairSpacingValue,
  useResolveBorderRadiusValue,
  useResolvePairSpacing,
} from '../hooks';
import styles from './wrapper.module.css';

export type WrapperProperties = {
  borderBottom?: boolean;
  borderBottomRadius?: BorderRadiusKey;
  borderLeft?: boolean;
  borderRight?: boolean;
  borderTop?: boolean;
  borderTopRadius?: BorderRadiusKey;
  marginHorizontal?: PairSpacingValue;
  marginVertical?: PairSpacingValue;
  paddingHorizontal?: PairSpacingValue;
  paddingVertical?: PairSpacingValue;
} & ComponentProps<'div'>;

const toLength = (spacing: number | undefined) => (spacing === undefined ? undefined : `${spacing}px`);

const Wrapper = ({
  borderBottom,
  borderBottomRadius,
  borderLeft,
  borderRight,
  borderTop,
  borderTopRadius,
  className,
  marginHorizontal,
  marginVertical,
  paddingHorizontal,
  paddingVertical,
  style,
  ...rest
}: WrapperProperties) => {
  const resolvePairSpacing = useResolvePairSpacing();
  const resolveBorderRadiusValue = useResolveBorderRadiusValue();

  const [paddingInlineStart, paddingInlineEnd] = resolvePairSpacing(paddingHorizontal);
  const [paddingBlockStart, paddingBlockEnd] = resolvePairSpacing(paddingVertical);
  const [marginInlineStart, marginInlineEnd] = resolvePairSpacing(marginHorizontal);
  const [marginBlockStart, marginBlockEnd] = resolvePairSpacing(marginVertical);

  const topRadius = resolveBorderRadiusValue(borderTopRadius);
  const bottomRadius = resolveBorderRadiusValue(borderBottomRadius);

  return (
    <div
      className={cn(className, styles.wrapper, {
        [styles['wrapper--border-bottom'] ?? '']: borderBottom,
        [styles['wrapper--border-left'] ?? '']: borderLeft,
        [styles['wrapper--border-right'] ?? '']: borderRight,
        [styles['wrapper--border-top'] ?? '']: borderTop,
      })}
      style={{
        ...style,
        ['--wrapper-margin-block-end' as string]: toLength(marginBlockEnd),
        ['--wrapper-margin-block-start' as string]: toLength(marginBlockStart),
        ['--wrapper-margin-inline-end' as string]: toLength(marginInlineEnd),
        ['--wrapper-margin-inline-start' as string]: toLength(marginInlineStart),
        ['--wrapper-padding-block-end' as string]: toLength(paddingBlockEnd),
        ['--wrapper-padding-block-start' as string]: toLength(paddingBlockStart),
        ['--wrapper-padding-inline-end' as string]: toLength(paddingInlineEnd),
        ['--wrapper-padding-inline-start' as string]: toLength(paddingInlineStart),
        ['--wrapper-radius-bottom-end' as string]: toLength(bottomRadius),
        ['--wrapper-radius-bottom-start' as string]: toLength(bottomRadius),
        ['--wrapper-radius-top-end' as string]: toLength(topRadius),
        ['--wrapper-radius-top-start' as string]: toLength(topRadius),
      }}
      {...rest}
    />
  );
};

export default Wrapper;
